// Menu Financeiro (2026-09-28).
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  gpsDriveFinanceService,
  type FinanceSummary,
  type FinanceRecurrence,
  type FinanceSeriesScope,
  type FinanceTransactionInput,
  type FinanceTransactionType,
} from "@/services/gpsdrive-finance.service";

export const useFinanceAccess = (enabled = true) =>
  useQuery({
    queryKey: ["finance", "access"],
    queryFn: gpsDriveFinanceService.access,
    enabled,
    staleTime: 60_000,
    retry: 1,
  });

// "Não carregar tudo de novo toda vez" (pedido 2026-09-28): o último painel
// de cada período fica salvo neste navegador e aparece na hora ao abrir;
// a busca no servidor roda por trás e só troca os números. Mesmo padrão do
// snapshot da lista de veículos/usuários. Guarda só os 2 últimos períodos (~450 KB cada numa conta grande).
const SNAPSHOT_KEY = "gpsdrive:finance-summary-cache";
const SNAPSHOT_MAX = 2;

function readSnapshot(from: string, to: string): FinanceSummary | undefined {
  try {
    const all = JSON.parse(localStorage.getItem(SNAPSHOT_KEY) ?? "{}") as Record<string, FinanceSummary>;
    return all[`${from}|${to}`];
  } catch {
    return undefined;
  }
}

function writeSnapshot(from: string, to: string, data: FinanceSummary) {
  try {
    const all = JSON.parse(localStorage.getItem(SNAPSHOT_KEY) ?? "{}") as Record<string, FinanceSummary>;
    delete all[`${from}|${to}`];
    const entries = [...Object.entries(all), [`${from}|${to}`, data] as const].slice(-SNAPSHOT_MAX);
    localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    // Sem espaço/modo privado: só perde a abertura instantânea.
  }
}

export const useFinanceSummary = (from: string, to: string, enabled = true) =>
  useQuery({
    queryKey: ["finance", "summary", from, to],
    queryFn: async () => {
      const data = await gpsDriveFinanceService.summary(from, to);
      writeSnapshot(from, to, data);
      return data;
    },
    enabled,
    staleTime: 60_000,
    retry: 1,
    initialData: () => readSnapshot(from, to),
    initialDataUpdatedAt: 0,
    // Troca de período: mantém o painel anterior na tela até o novo chegar.
    placeholderData: keepPreviousData,
    // Enquanto a cópia das faturas do Asaas está sendo feita, recarrega
    // sozinho (rápido na 1ª cópia, pra barra de progresso andar).
    refetchInterval: (query) => {
      const inv = query.state.data?.invoices;
      if (!inv || !("sync" in inv)) return false;
      if (inv.pending) return 3_000;
      return inv.sync.running ? 10_000 : false;
    },
  });

export const useFinanceTransactions = (from: string, to: string, type?: FinanceTransactionType, enabled = true) =>
  useQuery({
    queryKey: ["finance", "transactions", from, to, type ?? "all"],
    queryFn: () => gpsDriveFinanceService.listTransactions({ from, to, type }),
    enabled,
    retry: 1,
  });

function useInvalidateFinance() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["finance", "summary"] });
    qc.invalidateQueries({ queryKey: ["finance", "transactions"] });
  };
}

export function useCreateFinanceTransaction() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: ({ input, recurrence }: { input: FinanceTransactionInput; recurrence?: FinanceRecurrence | null }) =>
      gpsDriveFinanceService.createTransaction(input, recurrence ?? null),
    onSuccess: invalidate,
  });
}

export function useUpdateFinanceTransaction() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: ({ id, input, scope }: { id: string; input: Partial<FinanceTransactionInput>; scope?: FinanceSeriesScope }) =>
      gpsDriveFinanceService.updateTransaction(id, input, scope),
    onSuccess: invalidate,
  });
}

export function useDeleteFinanceTransaction() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: ({ id, scope }: { id: string; scope?: FinanceSeriesScope }) => gpsDriveFinanceService.deleteTransaction(id, scope),
    onSuccess: invalidate,
  });
}
